import type { Canvas } from 'fabric/fabric-impl';
import { RefObject } from 'react';
import { CustomFabricImage } from '../../../global-types';
import { calculateFabricCoordsCenter } from './calculate-fabric-coords-center';
import { checkIfImageIntersectsPhotozoneEdge } from './check-if-image-intersects-edge';
import { shrinkPt } from './shrink-pt';

/**
 * Arguments for applying force fill constraints to an image.
 */
type ApplyImageForceFillConstraintsArguments = {
  canvas: RefObject<Canvas> | undefined;
  image: CustomFabricImage;
  isFront: boolean;
  scale: number;
};

/**
 * Determines if force fill constraints should be applied to the image.
 * @param photoZoneID - The ID of the photo zone.
 * @param isFront - Whether the image is on the front side.
 * @returns {boolean} True if constraints should be applied, false otherwise.
 */
export const shouldApplyForceFillConstraints = ({
  photoZoneId,
  isFront,
}: {
  photoZoneId: string | undefined;
  isFront: boolean;
}): boolean => Boolean(photoZoneId && isFront);

/**
 * Finds the photo zone object on the canvas by its ID.
 * @param canvas - Ref to the fabric canvas.
 * @param photoZoneId - The ID of the photo zone to find.
 * @returns The photo zone object if found, otherwise undefined.
 */
export const findPhotoZoneById = ({
  canvas,
  photoZoneId,
}: {
  canvas: RefObject<Canvas> | undefined;
  photoZoneId: string | undefined;
}) => canvas?.current?.getObjects().find((obj) => obj.name === photoZoneId);

/**
 * Resets the image position to the center of its associated photo zone.
 * @param image - The fabric image object.
 * @param canvas - The canvas ref.
 */
export const resetImagePositionToPhotoZoneCenter = ({
  image,
  canvas,
}: {
  image: CustomFabricImage;
  canvas: RefObject<Canvas> | undefined;
}): void => {
  const photoZoneId = image?.data?.photoZoneId;
  const photoZoneObject = findPhotoZoneById({ canvas, photoZoneId });
  const photoZoneCenter = photoZoneObject?.getCenterPoint();
  if (photoZoneCenter) {
    image.setPositionByOrigin(photoZoneCenter, 'center', 'center');
  }
};

/**
 * Checks if a point is inside a polygon using the ray casting algorithm.
 * @param point - The point to check.
 * @param polygon - Array of polygon corner points.
 * @returns True if the point is inside the polygon, false otherwise.
 */
const isPointInPolygon = (point: { x: number; y: number }, polygon: Array<{ x: number; y: number }>): boolean => {
  return polygon.reduce((inside, vertex, i) => {
    const prevVertex = polygon.at(i === 0 ? -1 : i - 1) as { x: number; y: number };
    const isVertexAbove = vertex.y > point.y;
    const isPrevVertexAbove = prevVertex.y > point.y;
    const isCrossing = isVertexAbove !== isPrevVertexAbove;
    const xIntersect =
      ((prevVertex.x - vertex.x) * (point.y - vertex.y)) / (prevVertex.y - vertex.y || 1e-7) + vertex.x;
    return isCrossing && point.x < xIntersect ? !inside : inside;
  }, false);
};

/**
 * Checks if the image fully covers the photozone by verifying all photozone corners
 * are inside the image polygon.
 *
 * @param imageCoords - The four corners of the image.
 * @param photoZoneCoords - The four corners of the photozone (with or without margin).
 * @returns True if all photozone corners are inside the image, false otherwise.
 */
export const doesImageFullyCoverPhotozone = (
  imageCoords: Array<{ x: number; y: number }>,
  photoZoneCoords: Array<{ x: number; y: number }>,
): boolean => {
  return photoZoneCoords.every((corner) => isPointInPolygon(corner, imageCoords));
};

/**
 * Calculates the minimum uniform scale multiplier needed for a rotated image
 * (centered on the photozone) to fully cover all photozone margin corners.
 *
 * Works by transforming each photozone corner into the image's local (unrotated)
 * coordinate space and finding the largest ratio to the image half-dimensions.
 *
 * @param image - The fabric image (with current scale and rotation applied).
 * @param photoZoneMarginCoords - The photozone corners (with margin).
 * @returns A scale multiplier >= 1. If the image already covers, returns 1.
 */
export const getMinScaleForCoverage = (
  image: fabric.Image,
  photoZoneMarginCoords: Array<{ x: number; y: number }>,
): number => {
  const center = image.getCenterPoint();
  const angleRad = ((image.angle ?? 0) * Math.PI) / 180;
  const cosA = Math.cos(-angleRad);
  const sinA = Math.sin(-angleRad);
  const hw = ((image.width ?? 0) * (image.scaleX ?? 1)) / 2;
  const hh = ((image.height ?? 0) * (image.scaleY ?? 1)) / 2;

  if (hw === 0 || hh === 0) return 1;

  let minScale = 1;
  for (const corner of photoZoneMarginCoords) {
    const dx = corner.x - center.x;
    const dy = corner.y - center.y;
    // Transform into image's local (unrotated) coordinate system
    const lx = dx * cosA - dy * sinA;
    const ly = dx * sinA + dy * cosA;
    minScale = Math.max(minScale, Math.abs(lx) / hw, Math.abs(ly) / hh);
  }

  return minScale;
};

/**
 * Applies force fill constraints to an image on the canvas, ensuring it stays within the photo zone.
 * If the image is scaled down and any edge intersects the photo zone edge, resets its position.
 *
 * @param canvas - Ref to the fabric canvas.
 * @param image - The custom fabric image.
 * @param isFront - Whether the image is on the front side.
 * @param scale - The current scale value.
 */
export const applyImageForceFillConstraints = ({
  canvas,
  image,
  isFront,
  scale,
}: ApplyImageForceFillConstraintsArguments): void => {
  const photoZoneId: string | undefined = image?.data?.photoZoneId;
  if (!shouldApplyForceFillConstraints({ photoZoneId, isFront })) return;

  const photoZoneObject = findPhotoZoneById({ canvas, photoZoneId });
  if (!photoZoneObject) return;

  const imageCoords = image?.getCoords();
  const photoZoneCoords = photoZoneObject?.getCoords();
  const center = photoZoneCoords ? calculateFabricCoordsCenter(photoZoneCoords) : { x: 0, y: 0 };

  const photoZoneMarginCoords = (photoZoneCoords as Array<{ x: number; y: number }>)?.map((coord) =>
    shrinkPt({ center, pt: coord }),
  );

  // Check if any image edge intersects with any photozone edge (with margin)
  const anyEdgeIntersect = checkIfImageIntersectsPhotozoneEdge({
    imageCoords,
    photoZoneMarginCoords: photoZoneMarginCoords as [fabric.Point, fabric.Point, fabric.Point, fabric.Point],
  });

  // If any corner is inside or any edge intersects, revert to last valid position
  if (image.data.scaleValue > scale && anyEdgeIntersect) {
    resetImagePositionToPhotoZoneCenter({ image, canvas });
  }
};
